import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe, ChevronDown, Building2, Layers, ShieldCheck, Users, Handshake, Compass, Wrench, TrendingUp, Briefcase, Cog } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/context/I18nContext";

/** Smooth-scroll to a section id (with header offset). */
function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return false;
  const headerOffset = 116; // TopBar (36) + Navbar (80)
  const top = el.getBoundingClientRect().top + window.scrollY - headerOffset;
  window.scrollTo({ top, behavior: "smooth" });
  return true;
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // "company" | "services" | null
  const navigate = useNavigate();
  const location = useLocation();
  const { t, toggle } = useI18n();
  const menuRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClickOut = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenu(null);
    };
    document.addEventListener("mousedown", onClickOut);
    return () => document.removeEventListener("mousedown", onClickOut);
  }, []);

  // When the route lands on / with a hash, scroll after render.
  useEffect(() => {
    if (location.pathname === "/" && location.hash) {
      const id = location.hash.replace("#", "");
      // Wait a tick for sections to mount.
      const t1 = setTimeout(() => scrollToId(id), 80);
      const t2 = setTimeout(() => scrollToId(id), 350);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
    return undefined;
  }, [location.pathname, location.hash]);

  /** Smart navigation for hash links like "/#projects". */
  const goToHash = useCallback((href) => {
    if (!href || !href.startsWith("/#")) return;
    const id = href.slice(2);
    if (location.pathname === "/") {
      // Update the hash without history thrash, then scroll
      window.history.replaceState(null, "", `/#${id}`);
      scrollToId(id);
    } else {
      navigate(`/#${id}`);
    }
  }, [location.pathname, navigate]);

  const cm = t.nav.companyMenu;
  const sm = t.nav.servicesMenu;

  const companyItems = [
    { icon: Building2, label: cm.aboutSelf, href: "/#about" },
    { icon: Layers, label: cm.fields, href: "/#services" },
    { icon: ShieldCheck, label: cm.hse, href: "/#workforce" },
    { icon: Users, label: cm.clients, href: "/#clients" },
    { icon: Handshake, label: cm.partners, href: "/#clients" },
  ];
  const servicesItems = [
    { icon: Briefcase, label: sm.procurement, href: "/#services" },
    { icon: TrendingUp, label: sm.devFinance, href: "/#about" },
    { icon: Compass, label: sm.engineering, href: "/#electro-mechanical" },
    { icon: Cog, label: sm.epc, href: "/#services" },
    { icon: Wrench, label: sm.contracting, href: "/#services" },
  ];

  const linkBase = `text-sm font-medium gold-underline ${
    scrolled ? "text-foreground hover:text-primary" : "text-white/85 hover:text-white"
  }`;

  return (
    <header
      className={`fixed top-9 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-nav border-b border-border" : "bg-transparent"
      }`}
      data-testid="public-navbar"
    >
      <div className="container-page flex items-center justify-between h-20">
        <Link to="/" data-testid="navbar-logo-link">
          <Logo light={!scrolled} />
        </Link>

        <nav className="hidden lg:flex items-center gap-7" ref={menuRef}>
          {/* Company dropdown */}
          <DropdownTrigger
            label={t.nav.company}
            isOpen={openMenu === "company"}
            onClick={() => setOpenMenu(openMenu === "company" ? null : "company")}
            scrolled={scrolled}
            testid="nav-company"
          />
          {/* Services dropdown */}
          <DropdownTrigger
            label={t.nav.services}
            isOpen={openMenu === "services"}
            onClick={() => setOpenMenu(openMenu === "services" ? null : "services")}
            scrolled={scrolled}
            testid="nav-services"
          />
          <Link to="/#projects" className={linkBase} data-testid="nav-projects">{t.nav.projects}</Link>
          <Link to="/gallery" className={linkBase} data-testid="nav-gallery">{t.nav.gallery}</Link>
          <Link to="/#branches" className={linkBase} data-testid="nav-branches">{t.nav.branches}</Link>
          <Link to="/careers" className={linkBase} data-testid="nav-careers">{t.nav.careers}</Link>
          <Link to="/suppliers" className={linkBase} data-testid="nav-suppliers">{t.nav.suppliers}</Link>
          <Link to="/#contact" className={linkBase} data-testid="nav-contact">{t.nav.contact}</Link>
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={toggle} className={`gap-1.5 ${scrolled ? "" : "text-white hover:bg-white/10 hover:text-white"}`} data-testid="navbar-lang-toggle">
            <Globe className="h-4 w-4" />
            <span className="font-semibold">{t.other.label}</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate("/login")} className={scrolled ? "" : "text-white hover:bg-white/10 hover:text-white"} data-testid="navbar-login-btn">
            {t.nav.adminLogin}
          </Button>
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={toggle} className={scrolled ? "" : "text-white hover:bg-white/10 hover:text-white"} data-testid="navbar-mobile-lang-toggle">
            <Globe className="h-4 w-4" />
          </Button>
          <button onClick={() => setMobileOpen((v) => !v)} data-testid="navbar-mobile-toggle" aria-label="Toggle menu">
            {mobileOpen ? <X className={scrolled ? "text-foreground" : "text-white"} /> : <Menu className={scrolled ? "text-foreground" : "text-white"} />}
          </button>
        </div>
      </div>

      {/* Dropdown panel */}
      <AnimatePresence>
        {openMenu === "company" && (
          <DropdownPanel items={companyItems} title={t.nav.company} onClose={() => setOpenMenu(null)} goToHash={goToHash} />
        )}
        {openMenu === "services" && (
          <DropdownPanel items={servicesItems} title={t.nav.services} onClose={() => setOpenMenu(null)} goToHash={goToHash} />
        )}
      </AnimatePresence>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden bg-background border-t border-border overflow-hidden"
            data-testid="navbar-mobile-menu"
          >
            <div className="container-page py-6 flex flex-col gap-1">
              <MobileGroup title={t.nav.company} items={companyItems} onClose={() => setMobileOpen(false)} goToHash={goToHash} />
              <MobileGroup title={t.nav.services} items={servicesItems} onClose={() => setMobileOpen(false)} goToHash={goToHash} />
              <Link to="/#projects" onClick={(e) => { e.preventDefault(); setMobileOpen(false); goToHash("/#projects"); }} className="py-3 text-sm font-medium border-t border-border">{t.nav.projects}</Link>
              <Link to="/gallery" onClick={() => setMobileOpen(false)} className="py-3 text-sm font-medium border-t border-border">{t.nav.gallery}</Link>
              <Link to="/#branches" onClick={(e) => { e.preventDefault(); setMobileOpen(false); goToHash("/#branches"); }} className="py-3 text-sm font-medium border-t border-border">{t.nav.branches}</Link>
              <Link to="/careers" onClick={() => setMobileOpen(false)} className="py-3 text-sm font-medium border-t border-border">{t.nav.careers}</Link>
              <Link to="/suppliers" onClick={() => setMobileOpen(false)} className="py-3 text-sm font-medium border-t border-border">{t.nav.suppliers}</Link>
              <Link to="/#contact" onClick={(e) => { e.preventDefault(); setMobileOpen(false); goToHash("/#contact"); }} className="py-3 text-sm font-medium border-t border-border">{t.nav.contact}</Link>
              <Button onClick={() => { setMobileOpen(false); navigate("/login"); }} variant="outline" className="mt-3" data-testid="navbar-mobile-login">{t.nav.adminLogin}</Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function DropdownTrigger({ label, isOpen, onClick, scrolled, testid }) {
  return (
    <button
      onClick={onClick}
      className={`text-sm font-medium flex items-center gap-1.5 transition-colors ${
        scrolled ? "text-foreground hover:text-primary" : "text-white/85 hover:text-white"
      }`}
      data-testid={testid}
    >
      {label}
      <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
    </button>
  );
}

function DropdownPanel({ items, title, onClose, goToHash }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2 }}
      className="hidden lg:block absolute top-full left-0 right-0 bg-background border-y border-border shadow-2xl"
      data-testid={`dropdown-${title.toLowerCase()}`}
    >
      <div className="container-page py-10">
        <div className="overline mb-6">/ {title}</div>
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {items.map((it, i) => (
            <Link
              key={i}
              to={it.href}
              onClick={(e) => {
                if (it.href.startsWith("/#")) {
                  e.preventDefault();
                  goToHash(it.href);
                }
                onClose();
              }}
              className="group flex items-start gap-3 p-4 border border-border hover:border-primary hover:bg-muted/40 transition-colors"
              data-testid={`dropdown-item-${i}`}
            >
              <div className="h-10 w-10 bg-secondary text-primary flex items-center justify-center shrink-0">
                <it.icon className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold leading-snug group-hover:text-primary transition-colors">
                  {it.label}
                </div>
                <div className="mt-1 h-px w-6 bg-primary group-hover:w-12 transition-all duration-300" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

function MobileGroup({ title, items, onClose, goToHash }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-border">
      <button onClick={() => setOpen(!open)} className="w-full py-3 flex items-center justify-between text-sm font-semibold">
        {title}
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="pl-4 pb-3 flex flex-col gap-2">
          {items.map((it, i) => (
            <Link
              key={i}
              to={it.href}
              onClick={(e) => {
                if (it.href.startsWith("/#")) {
                  e.preventDefault();
                  goToHash(it.href);
                }
                onClose();
              }}
              className="text-sm text-muted-foreground py-2 hover:text-primary"
            >
              {it.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
